import { SEARCH_PARAMS_PAGE_VIEW_DEFAULT, SearchArticleViewOptions } from "@/constants/ui"
import { ProductPriceContainerCtnrROV1 } from "@ub/pricing-client"
import { create } from "zustand"

import { BasketData } from "@/lib/intershop/interfaces/basket/basket"
import { OrderData } from "@/lib/intershop/interfaces/order/order"
import { UserData } from "@/lib/intershop/interfaces/user/user"
import { ExtendedAddressRO } from "@/lib/storefront/addresses/address"
import { BasketLineItemData, BasketPromotionCode } from "@/lib/storefront/basket/basket"
import { ReducedProductData } from "@/lib/storefront/product/product"
import { RecoData } from "@/lib/storefront/reco/reco"

interface UbStorefrontState {
  isB2BCustomer: boolean
  setIsB2BCustomer: (isB2BCustomer: boolean) => void
  isOCI: boolean
  setIsOCI: (isOCI: boolean) => void
  addToBasketDialogVisible: boolean
  setAddToBasketDialogVisible: (visible: boolean) => void
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void
  loginFormUsername: string | undefined
  setLoginFormUsername: (loginFormUsername: string | undefined) => void
  loginFormPassword: string | undefined
  setLoginFormPassword: (loginFormPassword: string | undefined) => void
  userData?: UserData
  setUserData: (customerData: UserData | undefined) => void
  basketData: BasketData | undefined
  basketLineItems?: BasketLineItemData[]
  addresses?: ExtendedAddressRO[]
  basketPromotionCodes?: BasketPromotionCode[]
  setBasketData: (basket: BasketData | undefined) => void
  setBasketLineItems: (basketLineItems: BasketLineItemData[] | undefined) => void
  setAddresses: (addresses: ExtendedAddressRO[] | undefined) => void
  setBasketPromotionCodes: (basketPromotionCodes: BasketPromotionCode[] | undefined) => void
  productDataAddedToBasket: ReducedProductData | undefined
  setProductDataAddedToBasket: (productData: ReducedProductData | undefined) => void

  productDataAddedToBasketPrices: ProductPriceContainerCtnrROV1 | undefined
  setProductDataAddedToBasketPrices: (productPrices: ProductPriceContainerCtnrROV1 | undefined) => void
  filterShown: boolean
  setFilterShown: (filterShown: boolean) => void

  crosssellingProductDataAddedToBasket: RecoData[]
  setCrosssellingProductDataAddedToBasket: (productData: RecoData[]) => void

  createdOrder?: OrderData
  setCreatedOrder: (createdOrder: OrderData | undefined) => void

  customOrderId: string
  setCustomOrderId: (newCustomOrderId: string) => void

  messageToMerchant: string
  setMessageToMerchant: (newMessageToMerchant: string) => void

  basketPayments: any
  setBasketPayments: (basketPayments: any) => void

  basketAddresses: any
  setBasketAddresses: (basketAddresses: any) => void

  creditCheckConsentChecked: boolean
  setCreditCheckConsentChecked: (creditCheckConsentChecked: boolean) => void

  profileUpdated: boolean
  setProfileUpdated: (profileUpdated: boolean) => void

  subscriptionPending: boolean
  setSubscriptionPending: (subscriptionPending: boolean) => void

  articleListView: SearchArticleViewOptions
  setArticleListView: (articleListView: SearchArticleViewOptions) => void

  showAllPrices: boolean
  setShowAllPrices: (showAllPrices: boolean) => void
}

export const useUbStorefrontState = create<UbStorefrontState>((set) => ({
  // setIsB2BCustomer & setOCI are called from UBHeaderClient to make is accessible from "everywhere".
  isB2BCustomer: true,
  setIsB2BCustomer: (newIsB2BCustomer: boolean) => set(() => ({ isB2BCustomer: newIsB2BCustomer })),
  isOCI: false,
  setIsOCI: (newIsOCI: boolean) => set(() => ({ isOCI: newIsOCI })),

  addToBasketDialogVisible: false,
  setAddToBasketDialogVisible: (visible: boolean) => set(() => ({ addToBasketDialogVisible: visible })),

  isLoading: false,
  setIsLoading: (isLoading: boolean) => {
    //console.log("isLoading",isLoading)
    set(() => ({ isLoading }))
  },

  filterShown: false,
  setFilterShown: (filterShown: boolean) => set(() => ({ filterShown })),

  loginFormUsername: undefined,
  setLoginFormUsername: (loginFormUsername: string) => set(() => ({ loginFormUsername: loginFormUsername })),

  loginFormPassword: undefined,
  setLoginFormPassword: (loginFormPassword: string) => set(() => ({ loginFormPassword: loginFormPassword })),

  userData: undefined,
  setUserData: (userData: UserData) => set(() => ({ userData: userData })),

  basketData: undefined,
  setBasketData: (basketData: BasketData | undefined) => set(() => ({ basketData: basketData })),

  createdOrder: undefined,
  setCreatedOrder: (createdOrder: OrderData) => set(() => ({ createdOrder: createdOrder })),

  basketLineItems: undefined,
  setBasketLineItems: (basketLineItems: BasketLineItemData[]) => set(() => ({ basketLineItems: basketLineItems })),

  addresses: undefined,
  setAddresses: (addresses: ExtendedAddressRO[]) => set(() => ({ addresses: addresses })),

  basketPromotionCodes: undefined,
  setBasketPromotionCodes: (basketPromotionCodes: BasketPromotionCode[]) =>
    set(() => ({ basketPromotionCodes: basketPromotionCodes })),

  productDataAddedToBasket: undefined,
  setProductDataAddedToBasket: (productData: ReducedProductData | undefined) =>
    set(() => ({ productDataAddedToBasket: productData })),

  productDataAddedToBasketPrices: undefined,
  setProductDataAddedToBasketPrices: (productPrices: ProductPriceContainerCtnrROV1 | undefined) =>
    set(() => ({ productDataAddedToBasketPrices: productPrices })),

  crosssellingProductDataAddedToBasket: [],
  setCrosssellingProductDataAddedToBasket: (recoData: RecoData[]) =>
    set(() => ({ crosssellingProductDataAddedToBasket: recoData })),

  customOrderId: "",
  setCustomOrderId: (newCustomOrderId: string) => set(() => ({ customOrderId: newCustomOrderId })),

  messageToMerchant: "",
  setMessageToMerchant: (newMessageToMerchant: string) => set(() => ({ messageToMerchant: newMessageToMerchant })),

  basketPayments: undefined,
  setBasketPayments: (basketPayments: any) => set(() => ({ basketPayments: basketPayments })),

  basketAddresses: undefined,
  setBasketAddresses: (basketAddresses: any) => set(() => ({ basketAddresses: basketAddresses })),

  creditCheckConsentChecked: false,
  setCreditCheckConsentChecked: (creditCheckConsentChecked: boolean) => set(() => ({ creditCheckConsentChecked })),

  profileUpdated: false,
  setProfileUpdated: (profileUpdated: boolean) => set(() => ({ profileUpdated })),

  subscriptionPending: false,
  setSubscriptionPending: (subscriptionPending: boolean) => set(() => ({ subscriptionPending })),

  articleListView: SEARCH_PARAMS_PAGE_VIEW_DEFAULT,
  setArticleListView: (articleListView: SearchArticleViewOptions) => set(() => ({ articleListView })),

  showAllPrices: false,
  setShowAllPrices: (showAllPrices: boolean) => set(() => ({ showAllPrices })),
}))
